import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";
import { dashboardService } from "../../services/dashboardService";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import { DashboardStats } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";
import NotificationCenter from "../../components/NotificationCenter";
import { soundService } from "../../services/soundService";

const AdminDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { socket, joinDashboard, leaveDashboard } = useSocket();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);

  // RSVP Management State
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<keyof any>("submittedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Real-time notifications state (now handled by NotificationCenter internally)

  // Fetch dashboard stats
  const {
    data: dashboardStats,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => dashboardService.getStats(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Update local stats when data changes
  useEffect(() => {
    if (dashboardStats) {
      setStats(dashboardStats);
    }
  }, [dashboardStats]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (socket) {
      joinDashboard();

      socket.on("stats-updated", (newStats: DashboardStats) => {
        setStats(newStats);
      });

      socket.on("new-rsvp", (data: any) => {
        // Refetch stats when new RSVP comes in
        refetch();

        // Notifications are now handled by the NotificationCenter component

        // Play notification sound
        soundService.playNotificationSound();
      });

      socket.on("rsvp-updated", (data: any) => {
        // Refetch stats when RSVP is updated
        refetch();

        // Notifications are now handled by the NotificationCenter component

        // Play notification sound
        soundService.playNotificationSound();
      });

      socket.on("rsvp-deleted", (data: any) => {
        // Refetch stats when RSVP is deleted
        refetch();

        // Notifications are now handled by the NotificationCenter component

        // Play notification sound
        soundService.playNotificationSound();
      });

      return () => {
        socket.off("stats-updated");
        socket.off("new-rsvp");
        socket.off("rsvp-updated");
        socket.off("rsvp-deleted");
        leaveDashboard();
      };
    }
  }, [socket, joinDashboard, leaveDashboard, refetch]);

  const currentStats = stats || dashboardStats;

  // Filter and sort RSVPs
  const filteredAndSortedRSVPs = useMemo(() => {
    if (!currentStats?.recentRSVPs) return [];

    let filtered = currentStats.recentRSVPs.filter((rsvp) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        rsvp.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rsvp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (rsvp.company &&
          rsvp.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rsvp.role &&
          rsvp.role.toLowerCase().includes(searchTerm.toLowerCase()));

      // Attendance filter
      const matchesAttendance =
        attendanceFilter === "all" || rsvp.attendance === attendanceFilter;

      return matchesSearch && matchesAttendance;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortField as keyof typeof a];
      let bValue: any = b[sortField as keyof typeof b];

      if (sortField === "submittedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    currentStats?.recentRSVPs,
    searchTerm,
    attendanceFilter,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedRSVPs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRSVPs = filteredAndSortedRSVPs.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle sort
  const handleSort = (field: keyof any) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, attendanceFilter]);

  // Handle RSVP row click
  const handleRSVPClick = (rsvpId: string) => {
    navigate(`/admin/rsvp/${rsvpId}`);
  };

  // Notification management is now handled by NotificationCenter component

  // Analytics data processing
  const analyticsData = useMemo(() => {
    if (!currentStats?.recentRSVPs)
      return { trendsData: [], attendanceData: [], hourlyData: [] };

    const rsvps = currentStats.recentRSVPs;

    // 1. RSVP Trends over time (daily)
    const trendsMap = new Map();
    rsvps.forEach((rsvp) => {
      const date = new Date(rsvp.submittedAt).toISOString().split("T")[0];
      if (!trendsMap.has(date)) {
        trendsMap.set(date, {
          date,
          attending: 0,
          notAttending: 0,
          total: 0,
        });
      }
      const dayData = trendsMap.get(date);
      if (rsvp.attendance === "attending") {
        dayData.attending++;
      } else if (rsvp.attendance === "not-attending") {
        dayData.notAttending++;
      }
      dayData.total++;
    });

    const trendsData = Array.from(trendsMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // 2. Attendance breakdown for pie chart
    const attendanceCounts = {
      attending: rsvps.filter((r) => r.attendance === "attending").length,
      notAttending: rsvps.filter((r) => r.attendance === "not-attending")
        .length,
    };

    const attendanceData = [
      {
        name: "Attending",
        value: attendanceCounts.attending,
        color: "#10B981",
      },
      {
        name: "Not Attending",
        value: attendanceCounts.notAttending,
        color: "#EF4444",
      },
    ].filter((item) => item.value > 0);

    // 3. Hourly submission patterns
    const hourlyMap = new Map();
    rsvps.forEach((rsvp) => {
      const hour = new Date(rsvp.submittedAt).getHours();
      if (!hourlyMap.has(hour)) {
        // Convert to 12-hour format with AM/PM
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? "AM" : "PM";
        hourlyMap.set(hour, { hour: `${hour12}:00 ${ampm}`, count: 0 });
      }
      hourlyMap.get(hour).count++;
    });

    const hourlyData = Array.from(hourlyMap.entries())
      .map(([originalHour, data]) => ({ ...data, originalHour }))
      .sort((a, b) => a.originalHour - b.originalHour)
      .map(({ originalHour, ...data }) => data);

    return { trendsData, attendanceData, hourlyData };
  }, [currentStats?.recentRSVPs]);

  const handleExport = async (format: "csv" | "excel") => {
    try {
      const blob =
        format === "csv"
          ? await dashboardService.exportToCSV()
          : await dashboardService.exportToExcel();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rsvps.${format === "excel" ? "xlsx" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      // Export error handling - could show toast notification in production
    }
  };

  if (isLoading && !stats) {
    return <LoadingSpinner fullScreen text="Loading dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        className="bg-white border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <NotificationCenter />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.email}
                </p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <button
                onClick={logout}
                className="btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-2"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {/* Total RSVPs */}
          <motion.div
            className="card p-3 sm:p-4 lg:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-primary-100 rounded-lg flex-shrink-0">
                <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-primary-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Total RSVPs
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {currentStats?.totalRSVPs || 0}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Attending */}
          <motion.div
            className="card p-3 sm:p-4 lg:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-success-100 rounded-lg flex-shrink-0">
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-success-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Attending
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-success-600">
                  {currentStats?.attending || 0}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Not Attending */}
          <motion.div
            className="card p-3 sm:p-4 lg:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-danger-100 rounded-lg flex-shrink-0">
                <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-danger-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Not Attending
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-danger-600">
                  {currentStats?.notAttending || 0}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Actually Attended */}
          <motion.div
            className="card p-3 sm:p-4 lg:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Actually Attended
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                  {currentStats?.checkInCount || 0}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Attendance Rate */}
        <motion.div
          className="card p-3 sm:p-4 lg:p-6 mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
              Attendance Rate
            </h3>
            <span className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-900">
              {currentStats?.attendanceRate || 0}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div
              className="bg-primary-900 h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${currentStats?.attendanceRate || 0}%` }}
              transition={{ delay: 0.7, duration: 1 }}
            />
          </div>
        </motion.div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <motion.button
            onClick={() => handleExport("csv")}
            className="card p-3 sm:p-4 lg:p-6 text-left hover:shadow-medium transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-primary-100 rounded-lg flex-shrink-0">
                <ArrowDownTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-primary-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-900 truncate">
                  Export CSV
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                  Download guest list as CSV
                </p>
              </div>
            </div>
          </motion.button>

          <motion.button
            onClick={() => handleExport("excel")}
            className="card p-3 sm:p-4 lg:p-6 text-left hover:shadow-medium transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-success-100 rounded-lg flex-shrink-0">
                <ArrowDownTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-success-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-900 truncate">
                  Export Excel
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                  Download guest list as Excel
                </p>
              </div>
            </div>
          </motion.button>

          <motion.a
            href="/"
            target="_blank"
            className="card p-3 sm:p-4 lg:p-6 text-left hover:shadow-medium transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-primary-100 rounded-lg flex-shrink-0">
                <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-primary-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-900 truncate">
                  View Event
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                  See how guests see the event
                </p>
              </div>
            </div>
          </motion.a>

          

          <motion.button
            onClick={() => navigate("/admin/entrance")}
            className="card p-3 sm:p-4 lg:p-6 text-left hover:shadow-medium transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                <QrCodeIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-900 truncate">
                  Entrance Scanner
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                  Track actual attendance
                </p>
              </div>
            </div>
          </motion.button>
        </div>

        {/* RSVP Management Section */}
        {currentStats?.recentRSVPs && currentStats.recentRSVPs.length > 0 && (
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            {/* All RSVPs Section - 2/3 width */}
            <div className="lg:col-span-2">
              <motion.div
                className="card p-3 sm:p-4 lg:p-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
              >
                <div className="flex flex-col space-y-4 mb-6">
                  <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
                    All RSVPs ({filteredAndSortedRSVPs.length})
                  </h3>

                  {/* Search and Filter Controls */}
                  <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:gap-3">
                    {/* Search Bar */}
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, email, company"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
                      />
                    </div>

                    {/* Attendance Filter */}
                    <select
                      value={attendanceFilter}
                      onChange={(e) => setAttendanceFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent sm:w-auto w-full"
                    >
                      <option value="all">All Status</option>
                      <option value="attending">Attending</option>
                      <option value="not-attending">Not Attending</option>
                    </select>
                  </div>
                </div>

                {/* RSVPs Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("fullName")}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Name</span>
                            {sortField === "fullName" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon className="w-4 h-4" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                              ))}
                          </div>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                          Contact
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Company
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("attendance")}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Status</span>
                            {sortField === "attendance" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon className="w-4 h-4" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                              ))}
                          </div>
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 hidden sm:table-cell"
                          onClick={() => handleSort("submittedAt")}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date</span>
                            {sortField === "submittedAt" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon className="w-4 h-4" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                              ))}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedRSVPs.map((rsvp, index) => (
                        <motion.tr
                          key={rsvp._id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleRSVPClick(rsvp._id)}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.1 + index * 0.05 }}
                        >
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-2 h-2 rounded-full mr-3 ${
                                  rsvp.attendance === "attending"
                                    ? "bg-success-500"
                                    : "bg-danger-500"
                                }`}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {rsvp.fullName}
                                </div>
                                {rsvp.plusOne && rsvp.plusOneName && (
                                  <div className="text-xs text-gray-500">
                                    +1: {rsvp.plusOneName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap hidden sm:table-cell">
                            <div className="text-sm text-gray-900">
                              {rsvp.email}
                            </div>
                            {rsvp.phone && (
                              <div className="text-xs text-gray-500">
                                {rsvp.phone}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap hidden lg:table-cell">
                            <div className="flex items-center text-sm text-gray-900">
                              {rsvp.company && (
                                <>
                                  <BuildingOfficeIcon className="w-4 h-4 mr-1 text-gray-400" />
                                  {rsvp.company}
                                </>
                              )}
                            </div>
                            {rsvp.role && (
                              <div className="flex items-center text-xs text-gray-500">
                                <BriefcaseIcon className="w-3 h-3 mr-1" />
                                {rsvp.role}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                rsvp.attendance === "attending"
                                  ? "bg-success-100 text-success-800"
                                  : "bg-danger-100 text-danger-800"
                              }`}
                            >
                              {rsvp.attendance.replace("-", " ")}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                            {new Date(rsvp.submittedAt).toLocaleDateString()}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Showing {startIndex + 1} to{" "}
                      {Math.min(
                        startIndex + itemsPerPage,
                        filteredAndSortedRSVPs.length
                      )}{" "}
                      of {filteredAndSortedRSVPs.length} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Recent RSVPs Section - 1/3 width */}
            <div className="lg:col-span-1">
              <motion.div
                className="card p-3 sm:p-4 lg:p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
              >
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-4">
                  5 Most Recent RSVPs
                </h3>
                <div className="space-y-3">
                  {currentStats.recentRSVPs.slice(0, 5).map((rsvp, index) => (
                    <motion.div
                      key={rsvp._id}
                      className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleRSVPClick(rsvp._id)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 + index * 0.1 }}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div
                          className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                            rsvp.attendance === "attending"
                              ? "bg-success-500"
                              : "bg-danger-500"
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                            {rsvp.fullName}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {rsvp.email}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="text-xs font-medium text-gray-900 capitalize">
                          {rsvp.attendance.replace("-", " ")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(rsvp.submittedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Analytics & Charts Section */}
        {currentStats?.recentRSVPs && currentStats.recentRSVPs.length > 0 && (
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 sm:mb-8 mt-8 sm:mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
          >
            {/* RSVP Trends Line Chart */}
            <motion.div
              className="card p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.4 }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                RSVP Trends Over Time
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.trendsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="attending"
                      stroke="#10B981"
                      strokeWidth={2}
                      name="Attending"
                      dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="notAttending"
                      stroke="#EF4444"
                      strokeWidth={2}
                      name="Not Attending"
                      dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Attendance Breakdown Pie Chart */}
            <motion.div
              className="card p-4 sm:p-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.5 }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                Attendance Breakdown
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.attendanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analyticsData.attendanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Hourly Patterns Chart */}
        {currentStats?.recentRSVPs &&
          currentStats.recentRSVPs.length > 0 &&
          analyticsData.hourlyData.length > 0 && (
            <motion.div
              className="card p-4 sm:p-6 mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6 }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                RSVP Submission Patterns by Hour
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
